import { useState, useEffect, useCallback } from "react";
import styled from "styled-components";
import ReviewService from "../../services/ReviewService";
import StarRating from "./StarRating";
import Button from "../CommonComponents/Button";

const Section = styled.div`
  width: 100%;
  margin-top: 1.5rem;
  border-top: 1px solid ${({ theme }) => theme.colors.lineColor}44;
  padding-top: 1rem;
`;

const SectionTitle = styled.h3`
  font-size: 1rem;
  font-weight: 700;
  color: ${({ theme }) => theme.colors.primary};
  margin: 0 0 0.75rem;
`;

const Summary = styled.div`
  display: flex;
  align-items: center;
  gap: 0.75rem;
  margin-bottom: 1rem;
`;

const AvgNumber = styled.span`
  font-size: 1.75rem;
  font-weight: 800;
  color: ${({ theme }) => theme.colors.text};
  line-height: 1;
`;

const TotalLabel = styled.span`
  font-size: 0.8rem;
  color: ${({ theme }) => theme.colors.lineColor};
`;

const Form = styled.div`
  background: ${({ theme }) => theme.colors.background};
  border-radius: ${({ theme }) => theme.borderRadius.md};
  padding: 1rem;
  margin-bottom: 1rem;
`;

const FormLabel = styled.p`
  font-size: 0.85rem;
  font-weight: 600;
  color: ${({ theme }) => theme.colors.text};
  margin: 0 0 0.5rem;
`;

const Textarea = styled.textarea`
  width: 100%;
  box-sizing: border-box;
  padding: 0.5rem;
  border: 1px solid ${({ theme }) => theme.colors.lineColor};
  border-radius: ${({ theme }) => theme.borderRadius.sm};
  background: ${({ theme }) => theme.colors.card};
  color: ${({ theme }) => theme.colors.text};
  font-family: inherit;
  font-size: 0.9rem;
  resize: vertical;
  min-height: 70px;
  margin-top: 0.5rem;

  &:focus {
    outline: none;
    border-color: ${({ theme }) => theme.colors.primary};
  }
`;

const FormActions = styled.div`
  display: flex;
  gap: 0.5rem;
  margin-top: 0.75rem;
`;

const ReviewList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.75rem;
`;

const ReviewItem = styled.div`
  padding: 0.75rem;
  background: ${({ theme }) => theme.colors.background};
  border-radius: ${({ theme }) => theme.borderRadius.md};
`;

const ReviewHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  margin-bottom: 0.35rem;
  flex-wrap: wrap;
  gap: 0.35rem;
`;

const ReviewAuthor = styled.span`
  font-weight: 600;
  font-size: 0.875rem;
  color: ${({ theme }) => theme.colors.text};
`;

const ReviewDate = styled.span`
  font-size: 0.75rem;
  color: ${({ theme }) => theme.colors.lineColor};
`;

const ReviewComment = styled.p`
  margin: 0.35rem 0 0;
  font-size: 0.875rem;
  color: ${({ theme }) => theme.colors.text};
  line-height: 1.5;
`;

const OwnActions = styled.div`
  display: flex;
  gap: 0.4rem;
  margin-top: 0.5rem;
`;

const TextButton = styled.button`
  background: none;
  border: none;
  padding: 0;
  font-size: 0.8rem;
  color: ${({ danger, theme }) => (danger ? theme.status.error : theme.colors.primary)};
  cursor: pointer;
  font-family: inherit;
  &:hover { opacity: 0.75; }
`;

const ErrorMsg = styled.p`
  color: ${({ theme }) => theme.status.error};
  font-size: 0.8rem;
  margin: 0.4rem 0 0;
`;

function formatDate(dt) {
  if (!dt) return "";
  return new Date(dt).toLocaleDateString("hr-HR", { day: "2-digit", month: "2-digit", year: "numeric" });
}

function BookReviews({ bookId, user }) {
  const [reviews, setReviews] = useState([]);
  const [summary, setSummary] = useState(null);
  const [rating, setRating] = useState(0);
  const [comment, setComment] = useState("");
  const [editing, setEditing] = useState(false);
  const [error, setError] = useState("");
  const [submitting, setSubmitting] = useState(false);

  const myReview = user
    ? reviews.find((r) => r.userId === user.id)
    : null;

  const fetchAll = useCallback(async () => {
    const [r, s] = await Promise.all([
      ReviewService.getReviews(bookId),
      ReviewService.getSummary(bookId),
    ]);
    setReviews(r);
    setSummary(s);
  }, [bookId]);

  useEffect(() => {
    fetchAll();
  }, [fetchAll]);

  const handleSubmit = async () => {
    if (rating === 0) { setError("Odaberite ocjenu."); return; }
    setSubmitting(true);
    setError("");
    try {
      await ReviewService.addOrUpdate(bookId, rating, comment);
      setRating(0);
      setComment("");
      setEditing(false);
      await fetchAll();
    } catch (e) {
      setError(e.message);
    } finally {
      setSubmitting(false);
    }
  };

  const handleDelete = async () => {
    try {
      await ReviewService.deleteReview(bookId);
      await fetchAll();
    } catch (e) {
      setError(e.message);
    }
  };

  const startEdit = () => {
    if (myReview) {
      setRating(myReview.rating);
      setComment(myReview.comment || "");
      setEditing(true);
    }
  };

  const showForm = user && (!myReview || editing);

  return (
    <Section>
      <SectionTitle>Ocjene i recenzije</SectionTitle>

      {summary && summary.totalReviews > 0 && (
        <Summary>
          <AvgNumber>{summary.averageRating.toFixed(1)}</AvgNumber>
          <div>
            <StarRating value={Math.round(summary.averageRating)} readonly size="1.1rem" />
            <TotalLabel>{summary.totalReviews} {summary.totalReviews === 1 ? "recenzija" : "recenzija"}</TotalLabel>
          </div>
        </Summary>
      )}

      {showForm && (
        <Form>
          <FormLabel>{editing ? "Uredi svoju recenziju" : "Dodaj recenziju"}</FormLabel>
          <StarRating value={rating} onChange={setRating} />
          <Textarea
            placeholder="Komentar (nije obavezno)..."
            value={comment}
            onChange={(e) => setComment(e.target.value)}
          />
          {error && <ErrorMsg>{error}</ErrorMsg>}
          <FormActions>
            <Button
              tekst={submitting ? "Spremam..." : "Spremi"}
              akcija={handleSubmit}
              radius="6px"
              sirina="auto"
            />
            {editing && (
              <Button
                tekst="Odustani"
                akcija={() => { setEditing(false); setRating(0); setComment(""); setError(""); }}
                radius="6px"
                sirina="auto"
              />
            )}
          </FormActions>
        </Form>
      )}

      {!user && reviews.length === 0 && (
        <p style={{ fontSize: "0.85rem", color: "var(--color-line)" }}>
          Prijavite se kako biste ostavili recenziju.
        </p>
      )}

      <ReviewList>
        {reviews.map((r) => (
          <ReviewItem key={`${r.userId}-${r.bookId}`}>
            <ReviewHeader>
              <div>
                <ReviewAuthor>{r.userName}</ReviewAuthor>
                <ReviewDate style={{ marginLeft: "0.5rem" }}>{formatDate(r.createdAt)}</ReviewDate>
              </div>
              <StarRating value={r.rating} readonly size="1rem" />
            </ReviewHeader>
            {r.comment && <ReviewComment>{r.comment}</ReviewComment>}
            {user && r.userId === user.id && !editing && (
              <OwnActions>
                <TextButton onClick={startEdit}>Uredi</TextButton>
                <TextButton danger={1} onClick={handleDelete}>Obriši</TextButton>
              </OwnActions>
            )}
          </ReviewItem>
        ))}
      </ReviewList>
    </Section>
  );
}

export default BookReviews;
